import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { loginUser, verificationUser, verifyLogin } from "../model/LoginModel";
import Logger from "../../base/Logger";
import UserUtils from "../UserUtils";
import { useToast } from "../../components/provider/ToastProvider";

const { handleLoggedIn, getUserName } = UserUtils;

const LoginVM = () => {
  const { handleToastOpen } = useToast();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessedCallback = useRef(false);

  // Handle OAuth callback and URL parameters
  useEffect(() => {
    const handleOAuthCallback = async () => {
      // Prevent duplicate processing
      if (hasProcessedCallback.current) {
        console.log("Already processed OAuth callback, skipping...");
        return;
      }

      const urlParams = new URLSearchParams(location.search);
      const msauthParam = urlParams.get("msauth");
      const emailParam = urlParams.get("email");
      const errorParam = urlParams.get("error");
      const expectedEmail = urlParams.get("expected");
      const gotEmail = urlParams.get("got");

      console.log("=== URL Parameters ===");
      console.log("msauth:", msauthParam);
      console.log("email:", emailParam);
      console.log("error:", errorParam);

      // Handle error from Microsoft OAuth
      if (errorParam === "email_mismatch") {
        hasProcessedCallback.current = true;
        console.error("=== EMAIL MISMATCH ===");
        console.error("Expected:", expectedEmail);
        console.error("Got:", gotEmail);
        handleToastOpen("error", "Authentication failed. Please try again.");
        sessionStorage.removeItem("email");
        sessionStorage.removeItem("expected_email");
        navigate("/login", { replace: true });
        return;
      }

      if (errorParam === "session_error") {
        hasProcessedCallback.current = true;
        console.error("=== SESSION ERROR ===");
        handleToastOpen("error", "Authentication failed. Please try again.");
        sessionStorage.removeItem("email");
        sessionStorage.removeItem("expected_email");
        navigate("/login", { replace: true });
        return;
      }

      if (errorParam) {
        hasProcessedCallback.current = true;
        console.error("OAuth error:", errorParam);
        handleToastOpen("error", "Authentication failed. Please try again.");
        sessionStorage.removeItem("email");
        sessionStorage.removeItem("expected_email");
        navigate("/login", { replace: true });
        return;
      }

      // Handle successful Microsoft authentication
      if (msauthParam === "success" && emailParam) {
        hasProcessedCallback.current = true;
        console.log("=== Processing Microsoft Auth Success ===");
        console.log("Authenticated email:", emailParam);
        
        // 🔒 CLIENT-SIDE EMAIL VALIDATION
        const storedExpectedEmail = sessionStorage.getItem("expected_email");
        console.log("Expected email from storage:", storedExpectedEmail);
        console.log("Received email from OAuth:", emailParam);

        if (storedExpectedEmail) {
          const normalizedExpected = storedExpectedEmail.toLowerCase().trim();
          const normalizedReceived = emailParam.toLowerCase().trim();

          if (normalizedExpected !== normalizedReceived) {
            console.error("=== CLIENT-SIDE EMAIL MISMATCH DETECTED ===");
            console.error("Expected:", normalizedExpected);
            console.error("Received:", normalizedReceived);
            
            handleToastOpen(
              "error", 
              `Authentication failed. Please try again.`
            );
            
            sessionStorage.removeItem("email");
            sessionStorage.removeItem("expected_email");
            
            setLoading(false);
            navigate("/login", { replace: true });
            return;
          }
        }

        setLoading(true);

        try {
          console.log("Calling login API...");
          const result = await loginUser(emailParam);

          console.log("=== Login API Response ===");
          console.log("Full result:", JSON.stringify(result, null, 2));

          if (!result || !result.token) {
            throw new Error("Invalid response - no token");
          }

          console.log("Login successful, calling handleLoggedIn...");
          // Pass the full result object to handleLoggedIn
          handleLoggedIn(result);

          // Verify localStorage after handleLoggedIn
          console.log("=== localStorage After handleLoggedIn ===");
          console.log("token:", localStorage.getItem('token'));
          console.log("email:", localStorage.getItem('email'));
          console.log("userName:", localStorage.getItem('userName'));
          console.log("customerName:", localStorage.getItem('customerName'));
          console.log("terminal:", localStorage.getItem('terminal'));
          console.log("permissionData:", localStorage.getItem('permissionData'));

          sessionStorage.removeItem("email");
          sessionStorage.removeItem("expected_email");

          // Dispatch storage event
          console.log("Dispatching cStorage event...");
          const event = new CustomEvent("cStorage", {});
          window.dispatchEvent(event);

          handleToastOpen("success", "Login successful!");

          console.log("Navigating to /services...");

          // Redirect to services page
          setTimeout(() => {
            navigate("/services", { replace: true });
          }, 500);

        } catch (error) {
          console.error("=== Login Error ===");
          console.error("Error:", error);
          Logger.e("Login error:", error);
          handleToastOpen("error", "Authentication failed. Please try again.");
          sessionStorage.removeItem("email");
          sessionStorage.removeItem("expected_email");
          setLoading(false);
          navigate("/login", { replace: true });
        }

        return;
      }
    };

    handleOAuthCallback();
  }, [location.search, navigate, handleToastOpen]);

  // Verify existing login on component mount
  useEffect(() => {
    const verifyAndRedirect = async () => {
      const urlParams = new URLSearchParams(location.search);
      const hasOAuthParams = urlParams.has("msauth") || urlParams.has("error");

      // Skip verification if we have OAuth params
      if (hasOAuthParams) {
        console.log("OAuth params present, skipping auto-verification");
        return;
      }

      console.log("Checking for existing login...");
      const userName = getUserName();
      
      if (userName !== null && userName !== "") {
        console.log("Found existing login, verifying...");
        setLoading(true);
        
        try {
          await verifyLogin();
          console.log("Login verified successfully");
          setLoading(false);
          navigate("/services", { replace: true });
        } catch (error) {
          console.error("Verification failed:", error);
          Logger.e("Verification error:", error);
          setLoading(false);
        }
      } else {
        console.log("No existing login found");
      }
    };

    verifyAndRedirect();
  }, []);

  const handleLogin = async () => {
    if (!email || email.trim() === "") {
      handleToastOpen("error", "Please enter your email address");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      handleToastOpen("error", "Please enter a valid email address");
      return;
    }

    setLoading(true);

    try {
      const REACT_APP_BE_URL = process.env.REACT_APP_BE_URL;
      const normalizedEmail = email.toLowerCase().trim();
      
      console.log("=== Starting Login ===");
      console.log("Email:", normalizedEmail);
      console.log("Backend URL:", REACT_APP_BE_URL);

      // Verify user exists
      await verificationUser(normalizedEmail);
      console.log("User verification successful");

      // 🔒 Store expected email for validation when returning from OAuth
      sessionStorage.setItem("email", normalizedEmail);
      sessionStorage.setItem("expected_email", normalizedEmail);
      console.log("Stored expected email in sessionStorage:", normalizedEmail);

      // Get the base URL without query parameters
      const baseUrl = window.location.origin + window.location.pathname;

      // Construct Microsoft auth URL
      const redirectUrl = `${REACT_APP_BE_URL}/users/auth/microsoft?feURL=${encodeURIComponent(
        baseUrl
      )}&email=${encodeURIComponent(normalizedEmail)}`;

      console.log("Redirecting to Microsoft auth...");
      console.log("Redirect URL:", redirectUrl);

      // Reset the processed flag before redirecting
      hasProcessedCallback.current = false;

      // Redirect to Microsoft authentication
      window.location.href = redirectUrl;
    } catch (error) {
      console.error("Login initiation error:", error);
      Logger.e("Login initiation error:", error);
      handleToastOpen("error", "Authentication failed. Please try again.");
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    handleLogin,
    loading
  };
};

export default LoginVM;